import {
  doc,
  getDoc,
  collection,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "./config";

// ─── Collection name constants ────────────────────────────────────────────────
export const COLLECTIONS = {
  USERS: "users",
  ROOM_TYPES: "roomTypes",
  ROOMS: "rooms",
  BOOKINGS: "bookings",
  GUESTS: "guests",
  ADDONS: "addons",
  CONTENT: "content",
  BLOG: "blog",
  ACTIVITY_LOG: "activityLog",
} as const;

// ─── Well-known content document IDs ─────────────────────────────────────────
export const CONTENT_DOCS = {
  HOTEL_INFO: "hotel-info",
  HOMEPAGE: "homepage",
} as const;

// ─── Generic converter ────────────────────────────────────────────────────────
export function converter<T extends { id?: string }>() {
  return {
    toFirestore: (data: T): DocumentData => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: _id, ...rest } = data;
      return rest;
    },
    fromFirestore: (snap: QueryDocumentSnapshot): T =>
      ({ id: snap.id, ...snap.data() }) as T,
  };
}

// ─── Typed collection ref helper ──────────────────────────────────────────────
export function typedCollection<T extends { id?: string }>(path: string) {
  return collection(db, path).withConverter(converter<T>());
}

// ─── Typed document ref helper ────────────────────────────────────────────────
export function typedDoc<T extends { id?: string }>(path: string, id: string) {
  return doc(db, path, id).withConverter(converter<T>());
}

// ─── Fetch a single document (returns null if missing) ────────────────────────
export async function fetchDoc<T extends { id?: string }>(
  path: string,
  id: string,
): Promise<T | null> {
  const snap = await getDoc(typedDoc<T>(path, id));
  return snap.exists() ? snap.data() : null;
}
